import React from "react";
import PropTypes from "prop-types";
import { InputText } from "primereact/inputtext";
import { Skeleton } from "primereact/skeleton";
import { Tooltip } from "primereact/tooltip";
import { useForm, Controller } from "react-hook-form";
import { Calendar } from "primereact/calendar";
import { Chip } from "primereact/chip";
import { Chips } from "primereact/chips";
import ReactCountryFlag from "react-country-flag";
import { getName } from "country-list";
import { REQUEST_TYPES } from "../../useRequest";
import CustomeButton from "~/components/Button";
import { useNavigate } from "react-router-dom";
import useDateTime from "~/hooks/useDateTime";

const RequestDetailsSidebar = ({ data, loading, requestType }) => {
  const { control } = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: data,
  });
  const { formatDateTime } = useDateTime();

  const navigate = useNavigate();

  // const entityname = `${data?.entityData?.name} (${getName("IN")})`;

  const getInitials = (name) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    return parts.length === 1
      ? parts[0][0].toUpperCase()
      : (parts[0][0] + parts[1][0]).toUpperCase();
  };

  return (
    <div className="formgrid grid request-details pb-5 mb-5">
      <div className="col-12 pt-3">
        <Tooltip target=".info-icon" />
        <label className="input-label">Entity *</label>
        <div className="calendar-container mt-2">
          {loading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <div className="p-inputgroup">
              <div className="p-inputtext p-component p-disabled w-full entity-container">
                <div
                  className="flex align-items-center gap-2 entity-container-flow"
                  style={{
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <span className="entityname">{data?.entityData?.name}</span>
                  <span style={{ flexShrink: 0 }}>
                    (
                    <ReactCountryFlag
                      countryCode={data?.entityData?.country || ""}
                      svg
                      className=""
                    />{" "}
                    {getName(data?.entityData?.country || "")})
                  </span>
                </div>
              </div>
              <span className="p-inputgroup-addon icon-lock">
                <i className="pi pi-lock" data-testid="input-icon"></i>
              </span>
            </div>
          )}
        </div>
      </div>

      {requestType === REQUEST_TYPES.AD_HOC && (
        <div className="col-12 pt-3">
          <Tooltip target=".info-icon" />
          <label className="input-label">Service Line *</label>
          <div className="calendar-container mt-2">
            {loading ? (
              <Skeleton
                data-testid="skeleton-loader"
                className="p-4"
              ></Skeleton>
            ) : (
              <Controller
                name="entity"
                control={control}
                render={({ field }) => (
                  <>
                    <InputText
                      {...field}
                      value={data?.serviceData?.name}
                      placeholder="Service Line"
                      type="text"
                      name="service"
                      disabled
                      className="appearance-none outline-none w-full input"
                    />
                    <span
                      className="pi pi-lock input-icon"
                      data-testid="input-icon"
                    ></span>
                  </>
                )}
              />
            )}
          </div>
        </div>
      )}

      {requestType === REQUEST_TYPES.ESCALATION && (
        <div className="col-12 pt-3">
          <Tooltip target=".info-icon" />
          <label className="input-label">Process *</label>
          <div className="calendar-container mt-2">
            {loading ? (
              <Skeleton
                data-testid="skeleton-loader"
                className="p-4"
              ></Skeleton>
            ) : (
              <Controller
                name="process"
                control={control}
                render={({ field }) => (
                  <>
                    <InputText
                      {...field}
                      value={
                        data?.serviceData?.name === "Payroll +"
                          ? data?.custom_process_name
                          : data?.processData?.name
                      }
                      type="text"
                      name="process"
                      disabled
                      className="appearance-none outline-none w-full input truncate-process-name"
                    />
                    <span
                      className="pi pi-lock input-icon"
                      data-testid="input-icon"
                    ></span>
                  </>
                )}
              />
            )}
          </div>
        </div>
      )}

      <div className="col-12 pt-3">
        <Tooltip target=".info-icon" />
        <label className="input-label">
          Submitted
          <span
            data-pr-tooltip="The date and time by which this process must be completed."
            className="info-icon ml-1 pi pi-info-circle"
          />
        </label>
        <div className="calendar-container mt-2">
          {loading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="createdAt"
              control={control}
              render={({ field }) => {
                const display = data?.createdAt
                  ? formatDateTime({
                      date: data?.createdAt,
                      dateformat: "MMMM do, yyyy",
                    })
                  : formatDateTime({
                      date: field.value,
                      dateformat: "MMMM do, yyyy",
                    });

                const isDisabled = true;

                return (
                  <>
                    {isDisabled ? (
                      <InputText
                        value={display}
                        readOnly
                        className="w-full request__calander__input"
                        placeholder="Select date"
                        disabled
                      />
                    ) : (
                      <Calendar
                        {...field}
                        value={
                          data?.createdAt
                            ? new Date(data?.createdAt)
                            : field.value
                        }
                        disabled
                        dateFormat="MM d, yy"
                        placeholder="Select date"
                        className="w-full calander-createdat"
                        data-testid="lsp-select-date "
                      />
                    )}
                    <span
                      className="pi pi-lock input-icon"
                      data-testid="input-icon"
                    ></span>
                  </>
                );
              }}
            />
          )}
        </div>
      </div>

      <div className="field col-12 mb-0 mt-3">
        <label className="input-label">Submitted By</label>
        {loading ? (
          <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
        ) : (
          <div className="icon-field">
            <div className="appearance-none outline-none w-full input flex align-items-center p-2">
              <Chip
                className="assignee-chip"
                label={
                  <div className="flex align-items-center gap-2">
                    {data?.createdByData?.first_name +
                      " " +
                      data?.createdByData?.last_name}
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="p-icon p-chip-remove-icon"
                      role="button"
                      aria-hidden="true"
                      tabIndex="0"
                      data-pc-section="removeicon"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M7 14C5.61553 14 4.26215 13.5895 3.11101 12.8203C1.95987 12.0511 1.06266 10.9579 0.532846 9.67879C0.00303296 8.3997 -0.13559 6.99224 0.134506 5.63437C0.404603 4.2765 1.07129 3.02922 2.05026 2.05026C3.02922 1.07129 4.2765 0.404603 5.63437 0.134506C6.99224 -0.13559 8.3997 0.00303296 9.67879 0.532846C10.9579 1.06266 12.0511 1.95987 12.8203 3.11101C13.5895 4.26215 14 5.61553 14 7C14 8.85652 13.2625 10.637 11.9497 11.9497C10.637 13.2625 8.85652 14 7 14ZM7 1.16667C5.84628 1.16667 4.71846 1.50879 3.75918 2.14976C2.79989 2.79074 2.05222 3.70178 1.61071 4.76768C1.16919 5.83358 1.05367 7.00647 1.27876 8.13803C1.50384 9.26958 2.05941 10.309 2.87521 11.1248C3.69102 11.9406 4.73042 12.4962 5.86198 12.7212C6.99353 12.9463 8.16642 12.8308 9.23232 12.3893C10.2982 11.9478 11.2093 11.2001 11.8502 10.2408C12.4912 9.28154 12.8333 8.15373 12.8333 7C12.8333 5.45291 12.2188 3.96918 11.1248 2.87521C10.0308 1.78125 8.5471 1.16667 7 1.16667ZM4.66662 9.91668C4.58998 9.91704 4.51404 9.90209 4.44325 9.87271C4.37246 9.84333 4.30826 9.8001 4.2544 9.74557C4.14516 9.6362 4.0838 9.48793 4.0838 9.33335C4.0838 9.17876 4.14516 9.0305 4.2544 8.92113L6.17553 7L4.25443 5.07891C4.15139 4.96832 4.09529 4.82207 4.09796 4.67094C4.10063 4.51982 4.16185 4.37563 4.26872 4.26876C4.3756 4.16188 4.51979 4.10066 4.67091 4.09799C4.82204 4.09532 4.96829 4.15142 5.07887 4.25446L6.99997 6.17556L8.92106 4.25446C9.03164 4.15142 9.1779 4.09532 9.32903 4.09799C9.48015 4.10066 9.62434 4.16188 9.73121 4.26876C9.83809 4.37563 9.89931 4.51982 9.90198 4.67094C9.90464 4.82207 9.84855 4.96832 9.74551 5.07891L7.82441 7L9.74554 8.92113C9.85478 9.0305 9.91614 9.17876 9.91614 9.33335C9.91614 9.48793 9.85478 9.6362 9.74554 9.74557C9.69168 9.8001 9.62748 9.84333 9.55669 9.87271C9.4859 9.90209 9.40996 9.91704 9.33332 9.91668C9.25668 9.91704 9.18073 9.90209 9.10995 9.87271C9.03916 9.84333 8.97495 9.8001 8.9211 9.74557L6.99997 7.82444L5.07884 9.74557C5.02499 9.8001 4.96078 9.84333 4.88999 9.87271C4.81921 9.90209 4.74326 9.91704 4.66662 9.91668Z"
                        fill="currentColor"
                      ></path>
                    </svg>
                  </div>
                }
                image={data?.createdByData?.profile_picture || undefined}
                icon={
                  !data?.createdByData?.profile_picture && (
                    <span className="chip-profile-icon">
                      {getInitials(
                        data?.createdByData?.first_name +
                          " " +
                          data?.createdByData?.last_name,
                      )}
                    </span>
                  )
                }
                removable={false}
                // onRemove={() => {}}
              />
            </div>
          </div>
        )}
      </div>

      <div className="field col-12 mb-0 mt-3">
        <label className="input-label">Assignee *</label>
        {loading ? (
          <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
        ) : (
          <div className="icon-field">
            <div className="appearance-none outline-none w-full input flex align-items-center p-2">
              <Chip
                className="assignee-chip"
                label={
                  <div className="flex align-items-center gap-2">
                    {data?.assigneeData?.first_name +
                      " " +
                      data?.assigneeData?.last_name}
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="p-icon p-chip-remove-icon"
                      role="button"
                      aria-hidden="true"
                      tabIndex="0"
                      data-pc-section="removeicon"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M7 14C5.61553 14 4.26215 13.5895 3.11101 12.8203C1.95987 12.0511 1.06266 10.9579 0.532846 9.67879C0.00303296 8.3997 -0.13559 6.99224 0.134506 5.63437C0.404603 4.2765 1.07129 3.02922 2.05026 2.05026C3.02922 1.07129 4.2765 0.404603 5.63437 0.134506C6.99224 -0.13559 8.3997 0.00303296 9.67879 0.532846C10.9579 1.06266 12.0511 1.95987 12.8203 3.11101C13.5895 4.26215 14 5.61553 14 7C14 8.85652 13.2625 10.637 11.9497 11.9497C10.637 13.2625 8.85652 14 7 14ZM7 1.16667C5.84628 1.16667 4.71846 1.50879 3.75918 2.14976C2.79989 2.79074 2.05222 3.70178 1.61071 4.76768C1.16919 5.83358 1.05367 7.00647 1.27876 8.13803C1.50384 9.26958 2.05941 10.309 2.87521 11.1248C3.69102 11.9406 4.73042 12.4962 5.86198 12.7212C6.99353 12.9463 8.16642 12.8308 9.23232 12.3893C10.2982 11.9478 11.2093 11.2001 11.8502 10.2408C12.4912 9.28154 12.8333 8.15373 12.8333 7C12.8333 5.45291 12.2188 3.96918 11.1248 2.87521C10.0308 1.78125 8.5471 1.16667 7 1.16667ZM4.66662 9.91668C4.58998 9.91704 4.51404 9.90209 4.44325 9.87271C4.37246 9.84333 4.30826 9.8001 4.2544 9.74557C4.14516 9.6362 4.0838 9.48793 4.0838 9.33335C4.0838 9.17876 4.14516 9.0305 4.2544 8.92113L6.17553 7L4.25443 5.07891C4.15139 4.96832 4.09529 4.82207 4.09796 4.67094C4.10063 4.51982 4.16185 4.37563 4.26872 4.26876C4.3756 4.16188 4.51979 4.10066 4.67091 4.09799C4.82204 4.09532 4.96829 4.15142 5.07887 4.25446L6.99997 6.17556L8.92106 4.25446C9.03164 4.15142 9.1779 4.09532 9.32903 4.09799C9.48015 4.10066 9.62434 4.16188 9.73121 4.26876C9.83809 4.37563 9.89931 4.51982 9.90198 4.67094C9.90464 4.82207 9.84855 4.96832 9.74551 5.07891L7.82441 7L9.74554 8.92113C9.85478 9.0305 9.91614 9.17876 9.91614 9.33335C9.91614 9.48793 9.85478 9.6362 9.74554 9.74557C9.69168 9.8001 9.62748 9.84333 9.55669 9.87271C9.4859 9.90209 9.40996 9.91704 9.33332 9.91668C9.25668 9.91704 9.18073 9.90209 9.10995 9.87271C9.03916 9.84333 8.97495 9.8001 8.9211 9.74557L6.99997 7.82444L5.07884 9.74557C5.02499 9.8001 4.96078 9.84333 4.88999 9.87271C4.81921 9.90209 4.74326 9.91704 4.66662 9.91668Z"
                        fill="currentColor"
                      ></path>
                    </svg>
                  </div>
                }
                image={data?.assigneeData?.profile_picture || undefined}
                icon={
                  !data?.assigneeData?.profile_picture && (
                    <span className="chip-profile-icon">
                      {getInitials(
                        data?.assigneeData?.first_name +
                          " " +
                          data?.assigneeData?.last_name,
                      )}
                    </span>
                  )
                }
                removable={false}
              />
            </div>
          </div>
        )}
      </div>

      <div className="field col-12 mb-0 mt-3">
        <label className="input-label">CC</label>
        {loading ? (
          <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
        ) : (
          <div className="card p-fluid">
            <Chips
              className="cc-input"
              pt={{
                input: { className: "no-cursor" },
                container: { className: "no-pointer" },
              }}
              value={
                data?.ccusers && data?.ccusers?.length > 0
                  ? data?.ccusers?.map(
                      (user) => `${user?.first_name} ${user?.last_name}`,
                    )
                  : []
              }
            />
          </div>
        )}
      </div>

      {requestType === REQUEST_TYPES.ESCALATION &&
        data?.serviceData?.name !== "Payroll +" && (
          <div className="col-12 mb-0 mt-3">
            <CustomeButton
              label="View Process"
              variant="outlined"
              className="w-full"
              onClick={() =>
                navigate(`/process/${data?.processData?.id}/milestones`)
              }
            />
          </div>
        )}
    </div>
  );
};

RequestDetailsSidebar.propTypes = {
  data: PropTypes.object,
  loading: PropTypes.bool,
  requestType: PropTypes.string,
};
export default RequestDetailsSidebar;
